import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Chip,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { CustomerDatabaseStore } from "../../../../store/main/CustomerDatabaseStore";
import ReportFiltersComponent from "../ReportFiltersComponent";
import ReportScreen from "../../../../common/enums/ReportScreens";
import ReportFiltersLoading from "../ReportFiltersLoading";
import { ReportFilters } from "../../../../domain/models/report-filters";
import moment from "moment";
import { CalendarMonth, ViewList } from "@mui/icons-material";
import TourProgramCalendarView from "./sub-component/TourProgramCalendarView";
import TourProgramTableView from "./sub-component/TourProgramTableView";
import { MonthlyTourProgramStore } from "../../../../store/main/MonthlyTourProgramStore";
import {
  MonthlyHolidayData,
  MonthlyTourProgramData,
} from "../../../../domain/models/monthly-tour-program/fetch-monthly-tour-program";
import { TerritoryDataStore } from "../../../../store/main/TerritoryDataStore";
import { quickFilters } from "../../../../common/constants/tourProgramFilters";
import TourAlertMessageModal from "./Modal/TourAlertMessageModal";

const MonthlyTourProgramDesktop = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const { userDetails, decodeToken, userRole } = CustomerDatabaseStore();
  const { fetchMonthlyTourProgram } = MonthlyTourProgramStore();

  // Default month/year
  const currentDate = new Date();
  const defaultMonth = currentDate.getMonth() + 1;
  const defaultYear = currentDate.getFullYear();

  // State
  const [month, setMonth] = useState<number>(defaultMonth);
  const [year, setYear] = useState<number>(defaultYear);

  const [viewType, setViewType] = useState<"table" | "calendar">(
    () => (localStorage.getItem("monthlyTourView") as "table" | "calendar") || "table"
  );

  const [filterType, setFilterType] = useState<
    "All" | "Approved" | "Rejected" | "Approval Pending" | "Tour Not Set"
  >("All");

  const [tourData, setTourData] = useState<MonthlyTourProgramData[]>([]);
  const [isTourReportingEditable, setIsTourReportingEditable] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [holidayData, setHolidayData] = useState<MonthlyHolidayData[]>([]);
  const [specialAccessDays, setSpecialAccessDays] = useState<string[]>([]);
  const [showRefreshModal, setShowRefreshModal] = useState<boolean>(false);

  // Persist viewType to localStorage
  useEffect(() => {
    localStorage.setItem("monthlyTourView", viewType);
  }, [viewType]);


useEffect(() => {
  const isPageReloaded = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming;

  if (
    isPageReloaded?.type === "reload" &&
    viewType === "table"
  ) {
    setShowRefreshModal(true);
  }
}, []);

useEffect(() => {
  const alreadyShown = sessionStorage.getItem("tableRefreshModalShown");
  const nav = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming;

  if (!alreadyShown && nav?.type === "reload" && viewType === "table") {
    setShowRefreshModal(true);
  }
}, []);



  useEffect(() => {
    decodeToken();
    fetchTourData(defaultMonth, defaultYear);
  }, []);

  const handleViewChange = (_: any, val: "table" | "calendar" | null) => {
    if (!val) return;
    // Reset month/year when switching views
    setMonth(defaultMonth);
    setYear(defaultYear);
    setFilterType("All"); // <-- reset filters
    setViewType(val);
     // Fetch fresh tour data for the default month/year
  fetchTourData(defaultMonth, defaultYear);
  };

  const fetchTourData = async (selectedMonth: number, selectedYear: number) => {
    setLoading(true);
    try {
      const response = await fetchMonthlyTourProgram(selectedMonth, selectedYear);
      if (response) {
        setTourData(response.tours || []);
        setHolidayData(response.holidays || []);
        setIsTourReportingEditable(response.is_tour_reporting_editable);
        setSpecialAccessDays(response.special_access_days || []);
      }
    } catch (err) {
      console.error("Error fetching tour data:", err);
    } finally {
      setLoading(false);
    }
  };

const refreshData = async () => {
  setLoading(true);
  try {
    const response = await fetchMonthlyTourProgram(month, year);
    setTourData(response.tours || []);
    setHolidayData(response.holidays || []);
    setIsTourReportingEditable(response.is_tour_reporting_editable);
    setSpecialAccessDays(response.special_access_days || []);
  } catch (error) {
    console.error("Failed to refresh tour program data:", error);
  } finally {
    setLoading(false);
  }
};

useEffect(() => {
  refreshData();
}, [month, year]);

  const filterData = async (filter: ReportFilters) => {
    const month = parseInt(moment(filter.date?.toString()).format("MM"));
    const year = parseInt(moment(filter.date?.toString()).format("YYYY"));
    setMonth(month);
    setYear(year);
    fetchTourData(month, year);
  };

  const clearData = async () => {
    setMonth(defaultMonth);
    setYear(defaultYear);
    fetchTourData(defaultMonth, defaultYear);
  }


  const getFilteredData = () => {
    if (filterType === "All") return tourData;
    if (filterType === "Tour Not Set") return tourData.filter(item => item.status === null);

    const statusMap: Record<string, string> = {
      Approved: "active",
      Rejected: "in_active",
      "Approval Pending": "pending",
    };
    return tourData.filter(item => item.status !== null && item.status === statusMap[filterType]);
  };

  const filteredData = getFilteredData();

  const handleCloseModal = () => {
  setShowRefreshModal(false);
  sessionStorage.setItem("tableRefreshModalShown", "true");
};


  return (
    <Box px={isMobile ? 1 : 2} pt={1}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
        <Typography variant="h5" fontWeight={550}>Monthly Tour Program</Typography>
      </Box>

      {/* Filters + Toggle */}
      <Box mt={3} display="flex" flexDirection={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} flexWrap="wrap" gap={3}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} flexWrap="wrap" alignItems={{ xs: "stretch", md: "center" }} flex={1} minWidth={200}>
          <Box flex={1} minWidth={200}>
            {Object.keys(userDetails).length > 0 ? (
              <ReportFiltersComponent
                key={viewType} // <-- force remount on view switch
                type={ReportScreen.MONTHLY_TOUR_PROGRAM}
                successCallback={filterData}
                clearSuccessCallBack={clearData}
                userRole={userRole.role}
                userDetails={userDetails}
                gridColumns={14}
                territoryMultiSelect={false}
              />
            ) : <ReportFiltersLoading />}
          </Box>

          {viewType === "table" && (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" mt={{ xs: 1, md: 0 }}>
              <Typography variant="subtitle1" fontWeight={550}>Quick Filters:</Typography>
              {quickFilters.map(type => (
                <Chip
                  key={type}
                  label={type}
                  clickable
                  color={filterType === type ? "primary" : "default"}
                  onClick={() => setFilterType(type as typeof filterType)}
                  sx={{ fontWeight: filterType === type ? 600 : 400, borderRadius: "24px" }}
                />
              ))}
            </Stack>
          )}
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" mt={{ xs: 2, md: 0 }} justifyContent={{ xs: "flex-start", md: "flex-end" }}>
          <ToggleButtonGroup value={viewType} exclusive onChange={handleViewChange} size={isMobile ? "small" : "medium"}>
            {/* <ToggleButton value="table" sx={{ display: "flex", alignItems: "center", gap: 1 }}><ViewList fontSize="small" /></ToggleButton> */}
            {/* <ToggleButton value="calendar" sx={{ display: "flex", alignItems: "center", gap: 1 }}><CalendarMonth fontSize="small" /></ToggleButton> */}
          </ToggleButtonGroup>
        </Stack>
      </Box>

      
      {/* Main Table / Calendar */}
      <Box mt={2} width="100%" mb={7} display="flex" justifyContent="center">
        {loading ? (
          <CircularProgress size={60} thickness={5} />
        ) : (
          // ✅ Key based on viewType forces full remount
          <Box key={`${viewType}-${tourData.length}`} sx={{ width: "100%", maxWidth: 1200 }}>
            {viewType === "table" ? (
              <TourProgramTableView
                month={month}
                year={year}
                data={filteredData}
                holiday={holidayData}
                editable={isTourReportingEditable}
                specialAccessDays={specialAccessDays} 
                filterType={filterType}
                refreshData={refreshData}
              />
            ) : (
              <TourProgramCalendarView
                month={month}
                year={year}
                data={filteredData}
                holiday={holidayData}
                editable={isTourReportingEditable}
                specialAccessDays={specialAccessDays} 
                refreshData={refreshData}
              />
            )}
          </Box>
        )}
        <TourAlertMessageModal
  open={showRefreshModal}
  onClose={() => setShowRefreshModal(false)}
/>

      </Box>
    </Box>
  );
};

export default MonthlyTourProgramDesktop;
